import * as React from 'react'

import type { ILead, ILeadDetail } from '@user/store/lead/typings'
import { useAutoReviewPendingLeadsEnabled } from '@user/store/tenantFeatures/hooks/useAutoReviewPendingLeadsEnabled'

import { LeadCompanyRequiredFieldsDialog } from './LeadCompanyRequiredFieldsDialog'

interface ContextValue {
  hasMissing: boolean
  openUpdate: () => void
  /** Opens the modal when fields are missing and returns true; otherwise returns false and does nothing. */
  requestConvert: (navigate: () => void) => boolean
}

interface Props {
  lead: Pick<
    ILead | ILeadDetail,
    'id' | 'autoReviewed' | 'missingCompanyRequiredFields'
  >
  /** Refreshes the host's lead data; runs on save before the convert callback. */
  onRefresh?: () => void
  children: React.ReactNode
}

const NO_OP_VALUE: ContextValue = {
  hasMissing: false,
  openUpdate: () => undefined,
  requestConvert: () => false,
}

const LeadCompanyRequiredFieldsContext =
  React.createContext<ContextValue>(NO_OP_VALUE)

/**
 * Owns the single "Update Required Fields" modal for an auto-reviewed lead and shares its open state
 * with the banner and the Convert button. Outside a provider the hook is a no-op.
 */
export function LeadCompanyRequiredFieldsProvider(props: Props) {
  const { lead, onRefresh, children } = props

  const flagEnabled = useAutoReviewPendingLeadsEnabled()
  const missingFields = React.useMemo(
    () => lead.missingCompanyRequiredFields ?? [],
    [lead.missingCompanyRequiredFields],
  )
  const hasMissing =
    flagEnabled && lead.autoReviewed && missingFields.length > 0

  const [open, setOpen] = React.useState(false)
  const pendingConvert = React.useRef<(() => void) | null>(null)

  const openUpdate = React.useCallback(() => {
    pendingConvert.current = null
    setOpen(true)
  }, [])

  const requestConvert = React.useCallback(
    (navigate: () => void) => {
      if (!hasMissing) return false
      pendingConvert.current = navigate
      setOpen(true)
      return true
    },
    [hasMissing],
  )

  const onClose = React.useCallback(() => setOpen(false), [])

  const onSaved = React.useCallback(() => {
    onRefresh?.()
    const navigate = pendingConvert.current
    pendingConvert.current = null
    navigate?.()
  }, [onRefresh])

  const value = React.useMemo(
    () => ({ hasMissing, openUpdate, requestConvert }),
    [hasMissing, openUpdate, requestConvert],
  )

  return (
    <LeadCompanyRequiredFieldsContext.Provider value={value}>
      {children}
      <LeadCompanyRequiredFieldsDialog
        open={open}
        leadId={lead.id}
        missingFields={missingFields}
        onClose={onClose}
        onSaved={onSaved}
      />
    </LeadCompanyRequiredFieldsContext.Provider>
  )
}

LeadCompanyRequiredFieldsProvider.displayName =
  'LeadCompanyRequiredFieldsProvider'

export const useLeadCompanyRequiredFields = () =>
  React.useContext(LeadCompanyRequiredFieldsContext)
